import { AxiosError } from 'axios';

import KeyTypeEnum from '~enums/keyTypeEnum';
import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import { SkcDataResponse, SkcOffsetPagedResponse } from '~interfaces/responses';
import i18n from '~lib/i18n';
import ApiError from '~services/apiError';
import SkopeiConnectService from '~services/skopeiConnectService';

import Key from '../../../interfaces/key';

// FIXME: We need better handling for the different versions of the API.
// Right now we are setting version for whole service, we should be able to define it per endpoint/method.

class KeysService extends SkopeiConnectService {
	protected readonly path = 'keys';

	/**
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param searchQuery Text to search for in the key name, hardware id or bluetooth name.
	 * @param keyType Filter on key type.
	 * @param resourceGroupId Filter on resource group id.
	 * @param organisationId Filter on organisation id.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns An array of Keys.
	 */
	async getKeys(params: {
		page: string;
		pageSize: string;
		searchQuery?: string;
		keyType?: string;
		resourceGroupId?: string;
		organisationId?: string;
		signal?: AbortSignal;
	}): Promise<OffsetPagedResults<Key>> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client
			.get<SkcOffsetPagedResponse<KeyResponse>>(endpoint, {
				signal: params.signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					type: params.keyType,
					'client-id': params.organisationId,
					'resource-group-id': params.resourceGroupId,
					search: params.searchQuery?.trim() || undefined,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<Key>(Number(params.page), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, KeysService.fromResponse)
				);
			});
	}

	public getKeysCacheKey(
		searchParams: URLSearchParams,
		overwrittenClientId?: string | undefined,
		listType?: string | undefined
	): (string | URLSearchParams | undefined)[] {
		return [this.path, searchParams.toString(), overwrittenClientId, listType];
	}

	/**
	 * Gets a key by id.
	 *
	 * @param keyId The id of the key.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 *
	 * @returns The key.
	 */
	async getKeyById(keyId: string, organisationId?: string | undefined): Promise<Key> {
		const endpoint = this.createEndpoint('v2', [keyId]);
		const { data } = await this.client.get<KeyResponseLike | SkcDataResponse<KeyResponseLike>>(
			endpoint,
			{
				headers: {
					OverwrittenClientId: organisationId,
				},
			}
		);
		const payload = 'data' in data ? data.data : data;
		return KeysService.fromResponse(payload);
	}

	public getKeyByIdCacheKey(keyId?: string, overwrittenClientId?: string | undefined) {
		return [this.path, keyId, overwrittenClientId] as const;
	}

	/**
	 * Updates a key,
	 *
	 * @param keyId The id of the key that's to be updated.
	 * @param key The key with the new values.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 *
	 * @returns The updated key.
	 */
	async updateKey(
		keyId: string,
		key: Partial<Key>,
		organisationId?: string | undefined
	): Promise<Key> {
		const endpoint = this.createEndpoint('v1', [keyId]);
		return await this.client
			.put<SkcDataResponse<KeyResponse>>(endpoint, key, {
				headers: {
					OverwrittenClientId: organisationId,
				},
			})
			.then((response) => {
				if (!response || response.data == undefined) {
					const keyNotFoundError = new ApiError(new AxiosError(''));
					keyNotFoundError.setDescription(i18n.t('server.keyNotFoundException', { ns: 'errors' }));
					return Promise.reject(keyNotFoundError);
				}

				return Promise.resolve(KeysService.fromResponse(response.data.data));
			})
			.catch((error: ApiError) => {
				if (error.errorCode === 'SKC_011') {
					error.setDescription(i18n.t('keyTypeMismatch', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	/**
	 * Deletes a key.
	 *
	 * @param keyId The id of the key.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 *
	 * @returns The backend response.
	 */
	async deleteKey(keyId: string, organisationId?: string | undefined): Promise<void> {
		const endpoint = this.createEndpoint('v1', [keyId]);
		await this.client.delete(endpoint, {
			headers: {
				OverwrittenClientId: organisationId,
			},
		});
	}

	/**
	 * Model parsing.
	 */
	private static fromResponse(data: KeyResponseLike): Key {
		const payload = 'data' in data ? data.data : data;
		const { id, ...rest } = payload;

		return {
			...rest,
			id: payload.id.toString(),
			organisationId: payload.clientId?.toString(),
			organisationName: payload.clientName,
			resourceGroupId: payload.resourceGroupId?.toString() ?? payload.resourceGroup?.toString(),
		};
	}
}

interface KeyResponse {
	id: number;
	name: string;
	type: KeyTypeEnum;
	description?: string;
	idToken?: string;
	serialNumber?: string;
	resourceGroup?: string;
	resourceGroupId?: string;
	resourceGroupName?: string;
	clientId?: number;
	clientName?: string;
}

interface KeyResponseWithData {
	data: KeyResponse;
}

type KeyResponseLike = KeyResponse | KeyResponseWithData;

export default KeysService;
